import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Formik } from 'formik'
import { sortBy } from 'lodash'
import * as Yup from 'yup'

import { Button, DateTimePicker, MultiSelect, Input, Textarea, Checkbox, Form, Select } from 'components/Forms'
import { Collapsable } from 'components/UI'
import { Alert, List } from 'ui'
import { getAssignablePositions } from 'services/Structure'
import { getPositionName, hasAccessToThis } from 'services/Utilities'

import { t, currentLang } from 'translations'

const ScheduleTemplateInfoForm = ({ template, onSubmit, inModal, isNew, inProject }) => {
  const { _id: userId } = useSelector(state => state.users.userInfo)
  const { projectIsRequired, teamIsRequired } = useSelector(state => state.settings)
  const { departments, tags, projects, teams, positions, projectsById, nonFilterScheduleTypes, scheduleTypesById } = useSelector(state => state.resources)

  // Form validation
  let scheduleTemplateShape = {
    name: Yup.string().required('manager.scheduleTemplates.form.FIELD_MANDATORY'),
    projects: projectIsRequired ? Yup.string().required('manager.scheduleTemplates.form.FIELD_MANDATORY').nullable() : Yup.string().nullable(),
    team: teamIsRequired ? Yup.string().required('manager.scheduleTemplates.form.FIELD_MANDATORY').nullable() : Yup.string().nullable()
  }

  const history = useHistory()
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        name: '',
        comment: '',
        requiredPositions: isNew && inProject ? projectsById[inProject]?.positions : [],
        defaultShiftComment: '',
        defaultShiftTags: [],
        combineShiftAndTemplateTags: true,
        departments: [],
        allShiftsIrreplaceable: false,
        team: '',
        ...template,
        projects: inProject && isNew ? inProject : Array.isArray(template.projects) ? template.projects[0] : undefined,
        ...(isNew
          ? {
              bulkShiftPositions: positions.reduce((acc, { _id }) => ({ ...acc, [_id]: 0 }), {}),
              bulkStartTime: '09:00',
              bulkEndTime: '17:00'
            }
          : {})
      }}
      // Handle project field that is not an array in frontend
      onSubmit={({ projects, ...values }, actions) => {
        onSubmit({ ...values, ...(projects && projects.length ? { projects: [projects] } : {}) }, actions)
      }}
      validationSchema={Yup.object().shape(scheduleTemplateShape)}
    >
      {({ values, isSubmitting, status, setStatus, resetForm, dirty, setFieldValue }) => {
        return (
          <Form resetForm={resetForm} dirty={!!dirty && !isSubmitting} locationToGo={history[-2]} className="mt-2" inModal={inModal}>
            {({ leavingButton }) => (
              <>
                {/* Error message */}
                <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                  {status?.msg}
                </Alert>
                {/* Default information */}
                <Input module="manager.scheduleTemplates.form" name="name" required />
                <Textarea module="manager.scheduleTemplates.form" name="comment" />

                {/* Required positions for template */}
                <MultiSelect
                  module="manager.scheduleTemplates.form"
                  name="requiredPositions"
                  isMulti
                  isClearable
                  options={getAssignablePositions({ forMultiSelect: true })}
                />

                <MultiSelect
                  module="manager.scheduleTemplates.form"
                  name="projects"
                  options={projects.map(({ _id: value, name: label, projectOwners = [] }) => {
                    const canAssignProject = !hasAccessToThis('limitToReportsTo') || projectOwners.map(({ _id }) => _id).includes(userId)
                    return { label, value, isHidden: !canAssignProject }
                  })}
                  isClearable
                  handleChange={projectId => {
                    const { positions = [] } = projectsById[projectId] || {}
                    setFieldValue('requiredPositions', positions)
                  }}
                  required={projectIsRequired}
                />

                <MultiSelect
                  module="manager.scheduleTemplates.form"
                  name="team"
                  options={teams.map(({ _id: value, name: label, reportsTo = [] }) => {
                    const canAssignTeam = !hasAccessToThis('limitToReportsTo') || reportsTo.includes(userId)
                    return { label, value, isHidden: !canAssignTeam }
                  })}
                  isClearable
                  required={teamIsRequired}
                />

                {/* Options */}
                <Collapsable type="shiftOptions" title="manager.scheduleTemplates.form.OPTIONS">
                  {/* Default shift comment */}
                  <Textarea module="manager.scheduleTemplates.form" name="defaultShiftComment" isTranslatable />

                  {/* Default shift tags */}
                  <MultiSelect
                    module="manager.scheduleTemplates.form"
                    name="defaultShiftTags"
                    isMulti
                    isClearable
                    options={tags.map(({ _id, name, icon }) => ({ value: _id, label: name, icon }))}
                  />

                  {/* Combine shift and template tags */}
                  <Checkbox module="manager.scheduleTemplates.form" name="combineShiftAndTemplateTags" />

                  {/* Departments */}
                  <MultiSelect
                    module="manager.scheduleTemplates.form"
                    name="departments"
                    isMulti
                    isClearable
                    options={departments.map(({ _id, [`name${currentLang(true)}`]: label }) => ({ value: _id, label }))}
                  />

                  {/* Mark all shifts as irreplaceable */}
                  <Checkbox module="manager.scheduleTemplates.form" name="allShiftsIrreplaceable" />
                </Collapsable>

                {/* Bulk shift creation tool */}
                {isNew ? (
                  <Collapsable type="bulkShiftCreate" title="manager.scheduleTemplates.form.BULK_SHIFT_CREATE.TITLE" noPadding>
                    {!values.requiredPositions.length ? (
                      <div className="p-3">
                        <Alert variant="warning">{t('manager.scheduleTemplates.form.BULK_SHIFT_CREATE.NO_REQUIRED_POSITIONS')}</Alert>
                      </div>
                    ) : (
                      <>
                        <div className="p-3">
                          <DateTimePicker module="manager.scheduleTemplates.form.BULK_SHIFT_CREATE" name="bulkStartTime" required type="time" />
                          <DateTimePicker module="manager.scheduleTemplates.form.BULK_SHIFT_CREATE" name="bulkEndTime" required type="time" />
                          <Select
                            module="manager.scheduleTemplates.form.BULK_SHIFT_CREATE"
                            name="bulkScheduleType"
                            options={[
                              { _id: 'custom', name: t('manager.schedule.eventForm.CUSTOM_SCHEDULE_TYPE') },
                              ...sortBy(nonFilterScheduleTypes, 'startTime').map(scheduleType => ({
                                _id: scheduleType._id,
                                name: `${scheduleType.name} (${scheduleType.startTime} - ${scheduleType.endTime})`
                              }))
                            ]}
                            dontSort
                            handleChange={({ target: { value } }) => {
                              const { startTime = '09:00', endTime = '17:00' } = scheduleTypesById[value]
                              setFieldValue('bulkStartTime', startTime)
                              setFieldValue('bulkEndTime', endTime)
                            }}
                          />
                        </div>
                        <List className="list-group-flush list-group-flush-border">
                          {values.requiredPositions.map(positionId => {
                            return (
                              <List.Item key={positionId}>
                                <h6>{getPositionName(positionId)}</h6>
                                <Input
                                  module="manager.scheduleTemplates.form.BULK_SHIFT_CREATE"
                                  translation="amount"
                                  name={`bulkShiftPositions.${positionId}`}
                                  type="number"
                                  min="0"
                                  required
                                />
                              </List.Item>
                            )
                          })}
                        </List>
                      </>
                    )}
                  </Collapsable>
                ) : null}
                <div className="mt-2">
                  <Button className="btn btn-primary" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                  {leavingButton}
                </div>
              </>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default ScheduleTemplateInfoForm
